import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  Stack,
  Typography,
} from '@mui/material';

import { SketchViewModelType } from '@/context/MapContext/MapProvider';
import { useTranslation } from 'react-i18next';
import { getAreaLabel, getIntersectionAreaLabel, getKmLabel } from '@/helper/getArea';
import Scrollbar from 'react-scrollbars-custom';

import Graphic from '@arcgis/core/Graphic';
import * as webMercatorUtils from '@arcgis/core/geometry/support/webMercatorUtils';
import Polygon from '@arcgis/core/geometry/Polygon';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { getAreaFromCoordinates } from '../../utils/utils';

import React from 'react';
import { MixpanelTracking } from '@/services/mixpanel';
import { MixpanelEventHub } from '@/services/eventEnums';

export default function ListMonitorAoi({
  handleClose,
  sketchVM,
  previewLayer,
  intersectLayer,
  orderNewTab,
  areaRef,
  orderNewDataType,
  orderNewIsGuaranteedCapture,
  tabValue,
  isMonitorAoiLoading,
  monitorAoiList,
  view,
  setSelectedMonitorAoi,
  popoverContentRef,
  setIsMonitorAoiActive,
  openMonitorAoiPopover,
}: {
  handleClose: () => void;
  sketchVM: SketchViewModelType;
  previewLayer: any;
  intersectLayer: any;
  orderNewTab: any;
  areaRef: any;
  orderNewDataType: any;
  orderNewIsGuaranteedCapture: any;
  tabValue: any;
  isMonitorAoiLoading: boolean;
  monitorAoiList: any[];
  view: any;
  setSelectedMonitorAoi: React.Dispatch<React.SetStateAction<any>>;
  popoverContentRef: React.MutableRefObject<number>;
  setIsMonitorAoiActive: React.Dispatch<React.SetStateAction<boolean>>;
  openMonitorAoiPopover: () => void;
}) {
  const { t } = useTranslation('orderImagery');

  return (
    <Box className="popover-content" sx={{ marginTop: '0px !important' }}>
      <Stack direction="row" className="">
        <Button
          name="draw-rectangle-polygon-button"
          variant="outlined"
          color="secondary"
          className="return-back-btn"
          startIcon={<i style={{ fontSize: '11px' }} className="icon-rectangle-polygon"></i>}
          size="small"
          onClick={event => {
            event.stopPropagation();

            handleClose();
            //removing previous graphics if exist
            sketchVM.layer.removeAll();
            previewLayer.source.elements.removeAll();
            intersectLayer.removeAll();

            //for clearing the file polygon when clicking on draw rectangle polygon
            // MixpanelTracking.getInstance().clearProperty('file');
            // //for tracking
            MixpanelTracking.getInstance().track(
              MixpanelEventHub.HUB_MONITORED_AREA_NEW_MONITORED_AREA_CLICK
            );

            //getting the ref of area label
            let childNode = areaRef.current?.children[1];
            if (!childNode) return;
            //resetting the area value
            childNode.children[0].textContent = orderNewTab
              ? getIntersectionAreaLabel(
                  0,
                  t,
                  orderNewDataType,
                  tabValue,
                  orderNewIsGuaranteedCapture
                )
              : getAreaLabel(0, t);
            //clearing the intersection area value
            sketchVM.emit('update', { state: 'clearIntersectArea' });
            sketchVM.create('rectangle');

            // emitting the create event to open the popup
            sketchVM.emit('create', { state: 'monitorAoiComplete' });
          }}
        >
          {t('newMonitorArea', 'New Monitored Area')}
        </Button>
      </Stack>
      <List className="saved-areas-data">
        <Scrollbar className="scroll-thumb-color">
          {isMonitorAoiLoading ? (
            <Box className="text-center">
              <Typography variant="body1" className="mb-2">
                {t('gettingMonitoredAoi', 'Getting monitored AOIs ...')}
              </Typography>
              <CircularProgress thickness={6.5} />
            </Box>
          ) : monitorAoiList.length > 0 ? (
            monitorAoiList.map(value => (
              <ListItem
                sx={{ cursor: 'pointer' }}
                key={value.id}
                onClick={async () => {
                  //close the box
                  handleClose();

                  MixpanelTracking.getInstance().track(
                    MixpanelEventHub.HUB_MONITORED_AREA_ITEM_CLICK,
                    {
                      id: value?.id,
                    }
                  );
                  //get the coordinates by id
                  // eslint-disable-next-line require-use-server
                  const coordinates = value.location.coordinates;

                  //draw a polygon
                  const polygon = new Polygon({
                    rings: coordinates,
                    spatialReference: { wkid: 4326 },
                  });

                  let squarePolygon: any = null;
                  let circleGraphics: any = null;
                  let centerPointGraphics: any = null;
                  // //if sar flow is active
                  // if (sarSelectedRef.current === true) {
                  //     customToaster(t('sarInfoToast'), 'success');
                  //     //object is location: {x: num,y:num}
                  //     const location = { x: polygon.centroid.x, y: polygon.centroid.y };
                  //     let result = getCircleGraphicsAndSquarePolygonFromCenter(location);
                  //     squarePolygon = result.squarePolygon;
                  //     circleGraphics = result.circleGraphics;
                  //     centerPointGraphics = result.centerPointGraphic;
                  //     // bbox = squarePolygon.rings;
                  // }

                  // creating a graphic and showing it to a map
                  const graphic = new Graphic({
                    geometry: webMercatorUtils.geographicToWebMercator(polygon),
                    symbol: sketchVM?.polygonSymbol.clone(),
                  });
                  graphic.setAttribute('disable', true);
                  graphic.setAttribute('monitorAoi', true);

                  //remove previous grpahics
                  previewLayer.source.elements.removeAll();
                  sketchVM.layer.removeAll();

                  //add the new polygon graphic
                  sketchVM.layer.add(graphic);
                  //if the sar flow is active add the circle graphics
                  // sarSelectedRef.current && sketchVM.layer.add(circleGraphics),
                  sketchVM.layer.add(centerPointGraphics);

                  //firing the update event to set area in MapBox
                  // sarSelectedRef.current
                  //     ? sketchVM.update([circleGraphics, centerPointGraphics], { tool: 'move' })
                  //     :

                  sketchVM.update(graphic);

                  //firing the update event to initiate search

                  sketchVM.emit('update', {
                    state: 'active',
                    graphics: [graphic],
                    toolEventInfo: { type: 'move-stop' },
                  });

                  //emit the update event
                  sketchVM.emit('update', {
                    state: 'showSaveArea',
                    graphic,
                  });

                  await view.goTo({ target: polygon.extent }, { duration: 4000 });
                }}
              >
                {value.name}

                <Stack direction="row" alignItems="center" className="actions" spacing={2}>
                  <Typography variant="body2">
                    {getKmLabel(getAreaFromCoordinates(value.location.coordinates))}
                  </Typography>
                  <IconButton
                    onClick={async event => {
                      event.stopPropagation();
                      event.preventDefault();

                      popoverContentRef.current = 6;
                      setSelectedMonitorAoi(value);
                      const coordinates = value.location.coordinates;

                      //draw a polygon
                      const polygon = new Polygon({
                        rings: coordinates,
                        spatialReference: { wkid: 4326 },
                      });

                      const graphic = new Graphic({
                        geometry: webMercatorUtils.geographicToWebMercator(polygon),
                        symbol: sketchVM?.polygonSymbol.clone(),
                      });

                      //remove previous grpahics
                      previewLayer.source.elements.removeAll();
                      sketchVM.layer.removeAll();

                      sketchVM.layer.add(graphic);
                      sketchVM.update(graphic);
                      sketchVM.emit('update', {
                        state: 'showSaveArea',
                        graphic,
                      });

                      await view.goTo({ target: polygon.extent }, { duration: 4000 });

                      //add the new polygon graphic
                      MixpanelTracking.getInstance().track(
                        MixpanelEventHub.HUB_MONITORED_AREA_EDIT_CLICK,
                        {
                          id: value?.id,
                        }
                      );

                      // setMenuLoader({ id: value?.id, state: true });
                      // await handleMenuClick(event, value.name, value.id);
                    }}
                    size="small"
                  >
                    <EditOutlinedIcon />
                  </IconButton>
                </Stack>
              </ListItem>
            ))
          ) : (
            <Typography variant="body2" className="text-center">
              {t('noSavedAreas')}
            </Typography>
          )}
        </Scrollbar>
      </List>
    </Box>
  );
}
